import moment from 'moment';

/**
 * Moment only has a handful of localized formats, so we need to define
 * custom versions based on theirs here.
 * When a new language is added, that language should be added here, also.
 */
const MOMENT_CUSTOM_LOCALIZED_FORMATS = {
  // Localized Month Day. Ex: "January 9"
  // Same as LL, but without the year.
  LMD: {
    en: 'MMMM D',
    es: 'D [de] MMMM',
    fr: 'D MMMM',
    de: 'D. MMMM',
    pt: 'D [de] MMMM',
    it: 'D MMMM',
    sv: 'D MMMM',
    nl: 'D MMMM',
    ru: 'D MMMM',
    ja: 'M月D日',
    'zh-CN': 'M月D日',
    ar: 'D MMMM',
    'tl-ph': 'MMMM D',
    tr: 'D MMMM',
    vi: 'D MMMM',
  },
  // Localized Month Day Short. Ex: "Jan 9"
  // Same as LL, but without the year and an abbreviated month.
  LMDS: {
    en: 'MMM D',
    es: 'D [de] MMM',
    fr: 'D MMM',
    de: 'D. MMM',
    pt: 'D [de] MMM',
    it: 'D MMM',
    sv: 'D MMM',
    nl: 'D MMM',
    ru: 'D MMM',
    ja: 'M月D日',
    'zh-CN': 'M月D日',
    ar: 'D MMM',
    'tl-ph': 'MMM D',
    tr: 'D MMM',
    vi: 'D MMM',
  },
  // Localized Month Day Year Short. Ex: "Jan 9, 2019"
  // Same as LL, but with 3 M's instead of 4.
  LMDYS: {
    en: 'MMM D, YYYY',
    es: 'D [de] MMM [de] YYYY',
    fr: 'D MMM YYYY',
    de: 'D. MMM YYYY',
    pt: 'D [de] MMM [de] YYYY',
    it: 'D MMM YYYY',
    sv: 'D MMM YYYY',
    nl: 'D MMM YYYY',
    ru: 'D MMM YYYY г.',
    ja: 'YYYY年M月D日',
    'zh-CN': 'YYYY年M月D日',
    ar: 'D MMM YYYY',
    'tl-ph': 'MMM DD, YYYY',
    tr: 'D MMM YYYY',
    vi: 'D MMM [năm] YYYY',
  },
  // Localized Month Day Long with "at". Ex: "January 9 at 1:00 PM"
  // Similar to LLL, but with an "at" like designator between date and time.
  LLLA: {
    en: 'MMMM D, YYYY [at] h:mm A',
    es: 'D [de] MMMM [de] YYYY [a las] H:mm',
    fr: 'D MMMM YYYY [à] HH:mm',
    de: 'D. MMMM YYYY [um] HH:mm [Uhr]',
    pt: 'D [de] MMMM [de] YYYY [às] HH:mm',
    it: 'D MMMM YYYY [alle] HH:mm',
    sv: 'D MMMM YYYY [kl.] HH:mm',
    nl: 'D MMMM YYYY [om] HH:mm [uur]',
    ru: 'D MMMM YYYY г., H:mm',
    ja: 'YYYY年M月D日 HH:mm',
    'zh-CN': 'YYYY年M月D日Ah点mm分',
    ar: 'D MMMM YYYY HH:mm',
    'tl-ph': 'MMMM D, YYYY [sa] HH:mm',
    tr: 'D MMMM YYYY, HH:mm',
    vi: 'D MMMM [năm] YYYY [lúc] HH:mm',
  },
  // Localized Month Day Short with "at". Ex: "Jan 9 at 1:00 PM"
  // Similar to LLLA, but with a short Month.
  LMDSA: {
    en: 'MMM D, YYYY [at] h:mm A',
    es: 'D [de] MMM [de] YYYY [a las] H:mm',
    fr: 'D MMM YYYY [à] HH:mm',
    de: 'D. MMM YYYY [um] HH:mm [Uhr]',
    pt: 'D [de] MMM [de] YYYY [às] HH:mm',
    it: 'D MMM YYYY [alle] HH:mm',
    sv: 'D MMM YYYY [kl.] HH:mm',
    nl: 'D MMM YYYY [om] HH:mm [uur]',
    ru: 'D MMM YYYY г., H:mm',
    ja: 'YYYY年M月D日 HH:mm',
    'zh-CN': 'YYYY年M月D日Ah点mm分',
    ar: 'D MMM YYYY HH:mm',
    'tl-ph': 'MMM D, YYYY [sa] HH:mm',
    tr: 'D MMM YYYY, HH:mm',
    vi: 'D MMM [năm] YYYY [lúc] HH:mm',
  },
};

const customFormat = formatCode => {
  const currentLocale = moment.locale();
  const momentCustomFormat = MOMENT_CUSTOM_LOCALIZED_FORMATS[formatCode];
  if (momentCustomFormat[currentLocale]) {
    return momentCustomFormat[currentLocale];
  }
  // Fallback to English if the custom format isn't yet customized for
  // the current locale.
  return momentCustomFormat.en;
};

/**
 * This provides an interface to return the correct Moment.js format code based
 * on the current locale. This formats dates in a localized format relevant to
 * the viewer.
 *
 * @example
 *   moment.locale('en')
 *   moment(date).format(DateFormat.MONTH_DAY) // 'January 31'
 *   moment.locale('ru')
 *   moment(date).format(DateFormat.MONTH_DAY) // '31 января'
 */
const DateFormat = {
  /** @example moment(date).format(DateFormat.TIME) // '1:00 PM' */
  TIME: 'LT',

  /** @example moment(date).format(DateFormat.TIME_WITH_SECONDS) // '1:00:00 PM' */
  TIME_WITH_SECONDS: 'LTS',

  /** @example moment(date).format(DateFormat.SHORT) // '01/09/2019' */
  SHORT: 'L',

  /** @example moment(date).format(DateFormat.LONG) // 'January 9, 2019' */
  LONG: 'LL',

  /** @example moment(date).format(DateFormat.LONG_WITH_TIME) // 'January 9, 2019 1:00 PM' */
  LONG_WITH_TIME: 'LLL',

  /** @example moment(date).format(DateFormat.LONG_WITH_DAY_AND_TIME) // 'Wednesday, January 9, 2019 1:00 PM' */
  LONG_WITH_DAY_AND_TIME: 'LLLL',

  /** @example moment(date).format(DateFormat.LONG_AT_TIME) // 'January 9, 2019 at 1:00 PM' */
  get LONG_AT_TIME() {
    return customFormat('LLLA');
  },

  /** @example moment(date).format(DateFormat.LONG_AT_TIME) // 'January 9, 2019 at 1:00 PM' */
  get SHORT_AT_TIME() {
    return customFormat('LMDSA');
  },

  /** @example moment(date).format(DateFormat.MONTH_DAY) // 'January 9' */
  get MONTH_DAY() {
    return customFormat('LMD');
  },

  /** @example moment(date).format(DateFormat.MONTH_DAY_SHORT) // 'Jan 9' */
  get MONTH_DAY_SHORT() {
    return customFormat('LMDS');
  },

  /** @example moment(date).format(DateFormat.MONTH_DAY_YEAR_SHORT) // 'Jan 9, 2019' */
  get MONTH_DAY_YEAR_SHORT() {
    return customFormat('LMDYS');
  },

  /**
   * Fixed formats, meaning they don't change when the locale changes.
   * These are useful for ISO or matching formats to parse from the DB or a
   * library, such as a timepicker where the data format should never change.
   *
   * @example
   *   moment(date, DateFormat.fixed.ISO8601_LOCAL)
   */
  fixed: {
    /** @example moment(date).format(DateFormat.fixed.DATE_SELECT_TIME) // '13:33' */
    DATE_SELECT_TIME: 'HH:mm',

    /** @example moment(date).format(DateFormat.fixed.FULL_YEAR_MONTH_DATE_TIME) // '201901311:33:00 pm' */
    FULL_YEAR_MONTH_DATE_TIME: 'YYYYMMDDh:mm:ss a',

    /** @example moment(date).format(DateFormat.fixed.ISO8601_LOCAL) // '2019-01-31T13:33:00.000' */
    ISO8601_LOCAL: 'YYYY-MM-DDTHH:mm:ss.sss', // no time zone designator

    /** @example moment(date).format(DateFormat.fixed.NUMERIC_SHORT_MONTH_DAY_YEAR) // '01/31/19' */
    NUMERIC_SHORT_MONTH_DAY_YEAR: 'MM/DD/YY',

    /** @example moment(date).format(DateFormat.fixed.SHORT_MONTH_DAY_YEAR) // 'Jan 31, 2019' */
    SHORT_MONTH_DAY_YEAR: 'MMM D, YYYY',

    /** @example moment(date).format(DateFormat.fixed.DB_DATE) // '2019-01-31' */
    DB_DATE: 'YYYY-MM-DD',

    /** @example moment(date).format(DateFormat.fixed.SPREADSHEET_DATE) // '01/31/2019' */
    SPREADSHEET_DATE: 'MM/DD/YYYY',

    /** @example moment(date).format(DateFormat.fixed.LONG_WITH_SHORT_DAY_AND_TIME) // 'Wed, January 9, 2019 1:00 PM'*/
    LONG_WITH_SHORT_DAY_AND_TIME: 'ddd, MMMM D, YYYY h:mm A',

    /** @example moment(date).format(DateFormat.fixed.NUMBER_DAY) // '9'*/
    NUMBER_DAY: 'D',

    /** @example moment(date).format(DateFormat.fixed.LONG_MONTH) // 'January'*/
    LONG_MONTH: 'MMMM',

    /** @example moment(date).format(DateFormat.fixed.LONG_WITH_SHORT_DAY_AND_TIME) // 'Wed, January 9, 2019'*/
    LONG_WITH_SHORT_DAY: 'ddd, MMMM D, YYYY',
    /** @example moment(date).format(DateFormat.fixed.SHORT_DAY_WITH_SHORT_MONTH) // 'Wed, Jan 9, 2019'*/
    SHORT_DAY_WITH_SHORT_MONTH: 'ddd, MMM D, YYYY',
  },
};

export default DateFormat;
